import { inject, Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  CreateSiteContractSettingInput,
  GqlService,
  InvestigatorForecastSource,
  listOrganizationsWithInvestigatorCostCategoriesQuery,
  listSiteContractSettingsQuery,
  UpdateInvestigatorForecastInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { decimalAdd, decimalDifference, decimalDivide, decimalMultiply } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { InvestigatorAveragesService } from './investigator-averages.service';
import { InvestigatorContractedAmountService } from './investigator-contracted-amount.service';
import { InvestigatorPatientVisits } from './investigator-patient-visits.service';
import {
  AVERAGES_SOURCE_INITIAL_VALUES,
  PATIENT_VISITS_SOURCE_INITIAL_VALUE,
} from '../constants/investigator-forecast.const';
import {
  ContractedInvestigatorAmountValues,
  InvestigatorForecastValues,
} from '../types/investigator-forecast.types';

@Injectable({ providedIn: 'root' })
export class InvestigatorForecastService {
  private gqlService = inject(GqlService);
  private investigatorAveragesService = inject(InvestigatorAveragesService);
  private investigatorContractedAmountService = inject(InvestigatorContractedAmountService);
  private investigatorPatientVisits = inject(InvestigatorPatientVisits);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  averagesValues$ = new BehaviorSubject<listSiteContractSettingsQuery[]>([]);
  isLoadingSettings$ = new BehaviorSubject(false);

  getInvestigatorForecastAveragesAmounts(
    vendorId: string,
    contractedValues: ContractedInvestigatorAmountValues
  ) {
    return this.gqlService.listSiteContractSettings$(vendorId).pipe(
      map(({ data, errors }) => {
        if (errors.length || !data?.length) {
          this.averagesValues$.next([]);
          this.overlayService.error(errors);

          return {
            sourceLastUpdate: null,
            values: AVERAGES_SOURCE_INITIAL_VALUES,
          };
        }

        this.averagesValues$.next(data as listSiteContractSettingsQuery[]);

        return {
          sourceLastUpdate: first(data)?.source_last_updated || null,
          values: this.investigatorAveragesService.getInitialSummaryValues(
            data as listSiteContractSettingsQuery[],
            contractedValues
          ),
        };
      })
    );
  }

  getInvestigatorForecastContractedAmounts(vendorId: string) {
    return this.gqlService.listInvestigatorForecastContractedAmounts$(vendorId).pipe(
      map((response) => {
        const expenses = response.data || [];

        const sortedExpensesByDate = expenses.sort(
          ({ source_last_updated: date1 }, { source_last_updated: date2 }) =>
            Utils.dateSort((date1 || '1970-01-01') as string, (date2 || '1970-01-01') as string)
        );

        return {
          sourceLastUpdate: first(sortedExpensesByDate)?.source_last_updated || null,
          values: this.investigatorContractedAmountService.getSummaryValues(expenses),
        };
      })
    );
  }

  getInvestigatorForecastPatientVisitsAmounts(vendorId: string, spendToDate: number) {
    return this.gqlService.listActualPatientVisitSettings$(vendorId).pipe(
      map(({ data, errors }) => {
        if (errors.length || !data?.length) {
          this.averagesValues$.next([]);
          this.overlayService.error(errors);

          return {
            sourceLastUpdate: null,
            values: PATIENT_VISITS_SOURCE_INITIAL_VALUE,
          };
        }

        return {
          sourceLastUpdate: first(data)?.source_last_updated || null,
          values: this.investigatorPatientVisits.getInitialSummaryValues(data, spendToDate),
        };
      })
    );
  }

  getVendorListWithInvestigatorCosts(): Observable<
    listOrganizationsWithInvestigatorCostCategoriesQuery[]
  > {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => this.gqlService.listOrganizationsWithInvestigatorCostCategories$()),
      map((response) => {
        return response?.data || [];
      })
    );
  }

  savePatientVisits(formValues: InvestigatorForecastValues) {
    const { patientVisits, vendorId } = formValues;

    if (vendorId == null) {
      return;
    }

    const patientSettingsPromise = firstValueFrom(
      this.gqlService.createActualPatientVisitSetting$({
        discontinued_percent: patientVisits?.totalDiscontinuedPerc || 0,
        invoiceables_percent: patientVisits?.invoiceablesPerc || 0,
        organization_id: vendorId,
        other_percent: patientVisits?.invoiceablesDistribution || 0,
        overhead_percent: decimalDifference(100, patientVisits?.invoiceablesDistribution || 0),
        remaining_patients_to_discontinue_cost:
          patientVisits?.remainingPatientsToDiscontinueCost || 0,
        remaining_patients_to_discontinue_percent:
          patientVisits?.remainingPatientsToDiscontinuePerc || 0,
      })
    );
    const otherAmount = decimalMultiply(
      patientVisits.forecastedRemainingInvoiceables || 0,
      decimalDivide(patientVisits?.invoiceablesDistribution || 0, 100),
      2
    );

    const overheadAmount = decimalDifference(
      patientVisits.forecastedRemainingInvoiceables || 0,
      otherAmount,
      2
    );

    const patientVisitsAmount = decimalDifference(
      patientVisits.totalForecastedInvestigatorAmountThroughEndOfTrial,
      decimalAdd(otherAmount, overheadAmount, 2),
      2
    );

    const updateInvestigatorForecastPromise = firstValueFrom(
      this.updateInvestigatorForecast$(vendorId, {
        other_amount: otherAmount,
        overhead_amount: overheadAmount,
        patient_visits_amount: patientVisitsAmount,
        source: InvestigatorForecastSource.ACTUAL_PATIENT_VISITS,
      })
    );

    return Promise.all([patientSettingsPromise, updateInvestigatorForecastPromise]);
  }

  saveSiteContractAverages(formValues: InvestigatorForecastValues) {
    const { averages, vendorId } = formValues;

    if (vendorId == null) {
      return;
    }

    const createSiteContractPromise = firstValueFrom(
      this.saveSiteContractSetting$(vendorId, {
        average_patient_cost: averages?.averagePatientCost || 0,
        invoiceables_percent: averages?.invoiceables || 0,
        other_percent: averages?.otherInvoiceablesPerc || 0,
        overhead_percent: averages?.overheadInvoiceablesPerc || 0,
        total_forecasted_patients: averages?.totalForecastedPatients || 0,
      })
    );

    const otherAmount = decimalMultiply(
      averages.forecastedInvoiceables || 0,
      decimalDivide(averages?.otherInvoiceablesPerc || 0, 100),
      2
    );

    const overheadAmount = decimalDifference(averages.forecastedInvoiceables || 0, otherAmount, 2);

    const updateInvestigatorForecastPromise = firstValueFrom(
      this.updateInvestigatorForecast$(vendorId, {
        other_amount: otherAmount,
        overhead_amount: overheadAmount,
        patient_visits_amount: averages?.forecastedVisitCosts || 0,
        source: InvestigatorForecastSource.SITE_CONTRACT_AVERAGES,
      })
    );

    return Promise.all([createSiteContractPromise, updateInvestigatorForecastPromise]);
  }

  saveSiteContractSetting$(
    vendorId: string,
    data: Omit<CreateSiteContractSettingInput, 'organization_id'>
  ) {
    return this.gqlService.createSiteContractSetting$({
      organization_id: vendorId,
      ...data,
    });
  }

  updateInvestigatorForecast$(
    vendorId: string,
    data: Omit<UpdateInvestigatorForecastInput, 'organization_id'>
  ) {
    return this.gqlService.updateInvestigatorForecast$({
      organization_id: vendorId,
      ...data,
    });
  }
}
